import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { 
  Users, Search, Plus, Lock, Globe, ChevronRight, 
  Loader2, Filter, Grid, List, MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/Header';
import api from '@/utils/api';
import { toast } from 'sonner';

const GROUP_CATEGORIES = [
  { id: "all", name: "All Categories", icon: "📋" },
  { id: "buy_sell", name: "Buy & Sell", icon: "💰" },
  { id: "collectors", name: "Collectors", icon: "🏆" },
  { id: "local", name: "Local Community", icon: "📍" },
  { id: "fashion", name: "Fashion & Style", icon: "👗" },
  { id: "electronics", name: "Electronics", icon: "📱" },
  { id: "vehicles", name: "Vehicles", icon: "🚗" },
  { id: "home_garden", name: "Home & Garden", icon: "🏠" },
  { id: "sports", name: "Sports & Outdoors", icon: "⚽" },
  { id: "hobbies", name: "Hobbies & Crafts", icon: "🎨" },
  { id: "other", name: "Other", icon: "📦" },
];

const DEFAULT_COVER = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=300&fit=crop";

const GroupCard = ({ group, onJoin }) => {
  const navigate = useNavigate();
  const [joining, setJoining] = useState(false);
  const category = GROUP_CATEGORIES.find(c => c.id === group.category) || GROUP_CATEGORIES[GROUP_CATEGORIES.length - 1];

  const handleJoin = async (e) => {
    e.stopPropagation();
    setJoining(true);
    try {
      const res = await api.post(`/groups/${group.group_id}/join`);
      toast.success(res.data.message);
      if (onJoin) onJoin(group.group_id, res.data.status);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join group');
    } finally {
      setJoining(false);
    }
  };

  return (
    <Card 
      className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
      onClick={() => navigate(`/community/groups/${group.group_id}`)}
    >
      <div className="relative h-32">
        <img 
          src={group.cover_image || DEFAULT_COVER} 
          alt={group.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-2 left-3 right-3">
          <h3 className="font-semibold text-white line-clamp-1">{group.name}</h3>
          <div className="flex items-center gap-2 text-white/80 text-xs">
            {group.privacy === 'private' ? (
              <Lock className="h-3 w-3" />
            ) : (
              <Globe className="h-3 w-3" />
            )}
            <span>{group.privacy === 'private' ? 'Private' : 'Public'}</span>
            <span>•</span>
            <Users className="h-3 w-3" />
            <span>{group.member_count} members</span>
          </div>
        </div>
      </div>
      <CardContent className="p-3">
        <div className="flex items-center justify-between">
          <Badge variant="secondary" className="text-xs">
            {category.icon} {category.name}
          </Badge>
          {!group.is_member && (
            <Button 
              size="sm" 
              variant={group.privacy === 'private' ? 'outline' : 'default'}
              className="h-7 text-xs"
              onClick={handleJoin}
              disabled={joining}
            >
              {joining ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : group.privacy === 'private' ? (
                'Request to Join'
              ) : (
                'Join'
              )}
            </Button>
          )}
          {group.is_member && (
            <Badge className="bg-emerald-100 text-emerald-700">Member</Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2 line-clamp-2">{group.description}</p>
      </CardContent>
    </Card>
  );
};

const GroupsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [activeTab, setActiveTab] = useState('discover');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get('/auth/me');
        setUser(res.data);
      } catch {
        setUser(null);
      }
    };
    fetchUser();
  }, []);

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
      params.append('page', page.toString());
      params.append('limit', '12');

      const res = await api.get(`/groups?${params.toString()}`);
      setGroups(res.data.groups);
      setTotalPages(res.data.pages);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedCategory, page]);

  const fetchMyGroups = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get('/groups/my-groups');
      setMyGroups(res.data.groups);
    } catch (error) {
      console.error('Error fetching my groups:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    if (user) {
      fetchMyGroups();
    }
  }, [user, fetchMyGroups]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchGroups();
  };

  const handleJoinUpdate = (groupId, status) => {
    if (status === 'active') {
      fetchMyGroups();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header user={user} setUser={setUser} />
      
      {/* Spacer for fixed header - header is ~184px tall */}
      <div className="h-[184px]"></div>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">Community Groups</h1>
              <p className="text-emerald-100">
                Connect with like-minded buyers and sellers in specialized communities
              </p>
            </div>
            {user && (
              <Button 
                onClick={() => navigate('/community/groups/create')}
                className="bg-white text-emerald-600 hover:bg-emerald-50 font-semibold"
                size="lg"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Group
              </Button>
            )}
            {!user && (
              <Button 
                onClick={() => navigate('/auth')}
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                Sign in to Create Group
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Tabs and Filters Row */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            {/* Tabs */}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={activeTab === 'discover' ? 'default' : 'outline'}
                onClick={() => setActiveTab('discover')}
                className={activeTab === 'discover' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                <Globe className="h-4 w-4 mr-2" />
                Discover
              </Button>
              <Button
                variant={activeTab === 'my-groups' ? 'default' : 'outline'}
                onClick={() => user ? setActiveTab('my-groups') : navigate('/auth')}
                className={activeTab === 'my-groups' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                <Users className="h-4 w-4 mr-2" />
                My Groups {myGroups.length > 0 && `(${myGroups.length})`}
              </Button>
              
              {/* Create Group Button - Always visible */}
              <Button
                onClick={() => user ? navigate('/community/groups/create') : navigate('/auth')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Group
              </Button>
            </div>

            {/* Search & Category Filter */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search groups..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 w-full sm:w-56"
                  />
                </div>
                <Button type="submit" variant="secondary" size="icon">
                  <Search className="h-4 w-4" />
                </Button>
              </form>
              
              <Select value={selectedCategory} onValueChange={(val) => { setSelectedCategory(val); setPage(1); }}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {GROUP_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Category Quick Filters */}
        {activeTab === 'discover' && (
          <div className="flex flex-wrap gap-2 mb-6">
            {GROUP_CATEGORIES.slice(1, 8).map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => { setSelectedCategory(cat.id); setPage(1); }}
                className={selectedCategory === cat.id ? 'bg-emerald-600' : ''}
              >
                {cat.icon} {cat.name}
              </Button>
            ))}
          </div>
        )}

        {/* Content Area */}
        {activeTab === 'discover' ? (
          <>
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
              </div>
            ) : groups.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {groups.map(group => (
                    <GroupCard 
                      key={group.group_id} 
                      group={group} 
                      onJoin={handleJoinUpdate}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button 
                      variant="outline" 
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <span className="flex items-center px-4 text-sm text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      disabled={page === totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Card className="p-12 text-center">
                <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="font-semibold text-lg mb-2">No groups found</h3>
                <p className="text-gray-500 mb-4">
                  {searchQuery ? 'Try a different search term' : 'Be the first to create a group!'}
                </p>
                {user && (
                  <Button onClick={() => navigate('/community/groups/create')} className="bg-emerald-600">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Group
                  </Button>
                )}
              </Card>
            )}
          </>
        ) : (
          /* My Groups Tab */
          <>
            {!user ? (
              <Card className="p-12 text-center">
                <Lock className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="font-semibold text-lg mb-2">Sign in to see your groups</h3>
                <Button onClick={() => navigate('/auth')}>Sign In</Button>
              </Card>
            ) : myGroups.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {myGroups.map(group => (
                  <GroupCard key={group.group_id} group={{...group, is_member: true}} />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="font-semibold text-lg mb-2">You haven't joined any groups yet</h3>
                <p className="text-gray-500 mb-4">
                  Discover groups that match your interests
                </p>
                <Button onClick={() => setActiveTab('discover')} className="bg-emerald-600">
                  Discover Groups
                </Button>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GroupsPage;
